import React, { useState, useMemo } from 'react';
import { Box, Typography, Chip, Divider, Tooltip } from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { Field } from '@campfire/field';
import { Add, Email, Phone, Send, CheckCircle, RadioButtonUnchecked, Delete } from '@material-ui/icons';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { unpackToDate } from '@campfire/hot-date';
import { useApiUrl } from '../config/useApiUrl';
import { useCampfireFetch } from '../network/useCampfireFetch';
import { useSnackbar } from '../config/useSnackbar';
import { useUser } from '../auth/useUser';
import { useCampfireQuery } from '../network/useCampfireQuery';
import { SUYP_GET_REFEREE_CHECK } from './model/suyp-referee-check-query.gql';
import { SUYPGetRefereeCheck, SUYPGetRefereeCheckVariables } from './model/__generated__/SUYPGetRefereeCheck';
import { ConfirmationDialog } from '../../common/dialogs/ConfirmationDialog';

interface Props {
  taskId: string;
}

interface RefereeContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: 'pending' | 'invited' | 'completed';
  submissionToken?: string;
  dateSubmitted?: string;
  dateInvited?: string;
  dateLastReminded?: string;
  isCompleted: boolean;
}

const validationSchema = Yup.object({
  refereeName: Yup.string().required('Referee name is required'),
  refereeEmail: Yup.string()
    .email('Please enter a valid email address')
    .required('Referee email is required'),
  refereePhone: Yup.string(),
});

export const SUYPRefereeCheckTask: React.FC<Props> = ({ taskId }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [refereeToRemove, setRefereeToRemove] = useState<{ id: string; name: string } | null>(null);

  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const { user } = useUser();

  // API hooks for referee operations
  const addRefereeContactFetch = useCampfireFetch({ defer: true });
  const sendInvitationsFetch = useCampfireFetch({ defer: true });
  const removeRefereeContactFetch = useCampfireFetch({ defer: true });

  // GraphQL query to get referee check data
  const { data: refereeData, refetch } = useCampfireQuery<SUYPGetRefereeCheck, SUYPGetRefereeCheckVariables>(
    SUYP_GET_REFEREE_CHECK,
    {
      options: {
        variables: {
          taskId,
          profileId: user.profileId || '',
        },
        skip: !user.profileId,
      },
    }
  );

  // Transform GraphQL data into RefereeContact format
  const contacts = useMemo(() => {
    if (!refereeData?.vm.refereeCheck) return [];

    return refereeData.vm.refereeCheck.map((contact) => ({
      id: contact.completedTaskRefereeContactId,
      name: contact.refereeName,
      email: contact.refereeEmail,
      phone: contact.refereePhone || '',
      status: contact.isCompleted
        ? ('completed' as const)
        : contact.dateInvited
        ? ('invited' as const)
        : ('pending' as const),
      submissionToken: contact.submissionToken,
      dateSubmitted: contact.dateSubmitted || undefined,
      dateInvited: contact.dateInvited || undefined,
      dateLastReminded: contact.dateLastReminded || undefined,
      isCompleted: contact.isCompleted,
    }));
  }, [refereeData]);

  // Get required referee count and check if limit reached
  const requiredRefereeCount = refereeData?.vm.task?.refereeChecksRequired;
  const hasReachedLimit = requiredRefereeCount != null && contacts.length >= requiredRefereeCount;
  const requiredReferees = requiredRefereeCount || 0;

  // Calculate reminder button state
  const invitedContacts = contacts.filter((contact) => contact.dateInvited && !contact.isCompleted);
  const hasInvitedContacts = invitedContacts.length > 0;
  const uninvitedContacts = contacts.filter((contact) => !contact.dateInvited);
  const hasUninvitedContacts = uninvitedContacts.length > 0;

  // A reminder is due 24 hours after the last time a contact was invited or reminded
  const getReminderDueDate = (contact: RefereeContact) => {
    const lastContacted = contact.dateLastReminded || contact.dateInvited;
    if (!lastContacted) return null;
    return new Date(
      unpackToDate(lastContacted)
        .toJSDate()
        .getTime() +
        24 * 60 * 60 * 1000
    );
  };

  // Check if any invited contact is past its 24-hour cooldown since it was last invited/reminded
  const canSendReminder = useMemo(() => {
    if (!hasInvitedContacts) return false;

    const now = new Date();

    return invitedContacts.some((contact) => {
      const dueDate = getReminderDueDate(contact);
      return dueDate !== null && dueDate <= now;
    });
  }, [invitedContacts, hasInvitedContacts]);

  // Calculate when reminders can next be sent (the soonest any contact comes off cooldown)
  const reminderAvailableAt = useMemo(() => {
    if (!hasInvitedContacts) return null;

    const dueDates = invitedContacts
      .map((contact) => getReminderDueDate(contact))
      .filter((date): date is Date => date !== null)
      .sort((a, b) => a.getTime() - b.getTime());

    return dueDates.length > 0 ? dueDates[0] : null;
  }, [invitedContacts, hasInvitedContacts]);

  const getReminderTooltip = () => {
    if (!hasInvitedContacts) return '';
    if (canSendReminder) return "Send reminder emails to referees who haven't responded yet";
    if (reminderAvailableAt) {
      const now = new Date();
      const timeUntilAvailable = reminderAvailableAt.getTime() - now.getTime();
      if (timeUntilAvailable > 0) {
        const hoursUntilAvailable = Math.ceil(timeUntilAvailable / (1000 * 60 * 60));
        return `Reminders can be sent in ${hoursUntilAvailable} hour${hoursUntilAvailable !== 1 ? 's' : ''}`;
      }
    }
    return 'Must wait 24 hours since the last invitation or reminder to send another';
  };

  const handleAddReferee = async (values: {
    refereeName: string;
    refereeEmail: string;
    refereePhone: string;
    refereeMessage: string;
  }) => {
    try {
      const requestData: any = {
        taskId,
        profileId: user.profileId,
        refereeName: values.refereeName,
        refereeEmail: values.refereeEmail,
        refereePhone: values.refereePhone,
      };

      // Only include refereeMessage if it has content
      if (values.refereeMessage && values.refereeMessage.trim()) {
        requestData.refereeMessage = values.refereeMessage.trim();
      }

      const response = await addRefereeContactFetch.run({
        url: `${apiUrl}/vm/task/referee-contact/add`,
        method: 'post',
        data: requestData,
      });

      if (response.ok && response.data?.data) {
        setShowAddForm(false);
        setSnackbar({
          open: true,
          message: 'Referee contact added successfully',
          variant: 'success',
        });
        // Refetch the referee data to get the updated list
        if (refetch) {
          refetch();
        }
      } else {
        throw new Error('Failed to add referee contact');
      }
    } catch (error) {
      console.error('Error adding referee contact:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add referee contact. Please try again.',
        variant: 'error',
      });
    }
  };

  const handleSendInvitations = async () => {
    setIsSending(true);
    try {
      // Referees already invited need a reminder; the invitation endpoint only
      // ever sends to those who have never been contacted.
      const response = await sendInvitationsFetch.run({
        url: hasUninvitedContacts
          ? `${apiUrl}/vm/task/referee-check/send-invitations`
          : `${apiUrl}/vm/task/referee-check/send-reminder`,
        method: 'post',
        data: {
          taskId,
          profileId: user.profileId,
        },
      });

      if (response.ok && response.data?.data) {
        const responseData = response.data.data as { count?: number };
        const sentCount = responseData.count || 0;
        setSnackbar({
          open: true,
          message:
            (response.data as { message?: string }).message ||
            `Invitations sent to ${sentCount} referee${sentCount !== 1 ? 's' : ''}`,
          variant: 'success',
        });
        // Refetch the referee data to get updated statuses
        if (refetch) {
          refetch();
        }
      } else {
        throw new Error('Failed to send invitations');
      }
    } catch (error) {
      console.error('Error sending invitations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send invitations. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleRemoveReferee = async (refereeContactId: string, refereeName: string) => {
    setRefereeToRemove({ id: refereeContactId, name: refereeName });
    setRemoveDialogOpen(true);
  };

  const confirmRemoveReferee = async () => {
    if (!refereeToRemove) return;

    try {
      const response = await removeRefereeContactFetch.run({
        url: `${apiUrl}/vm/task/referee-contact/remove`,
        method: 'post',
        data: {
          refereeContactId: refereeToRemove.id,
        },
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: `${refereeToRemove.name} removed successfully`,
          variant: 'success',
        });
        // Refetch the referee data to get the updated list
        if (refetch) {
          refetch();
        }
        setRemoveDialogOpen(false);
        setRefereeToRemove(null);
      } else {
        throw new Error('Failed to remove referee contact');
      }
    } catch (error) {
      console.error('Error removing referee contact:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove referee contact. Please try again.',
        variant: 'error',
      });
      setRemoveDialogOpen(false);
      setRefereeToRemove(null);
    }
  };

  const getStatusStyle = (status: RefereeContact['status']) => {
    switch (status) {
      case 'completed':
        return {
          backgroundColor: '#e8f5e8',
          color: '#2e7d32',
          borderColor: '#2e7d32',
        };
      case 'invited':
        return {
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
          borderColor: '#1976d2',
        };
      default:
        return {
          backgroundColor: '#f5f5f5',
          color: '#616161',
          borderColor: '#9e9e9e',
        };
    }
  };

  const getStatusLabel = (status: RefereeContact['status']) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'invited':
        return 'Sent';
      default:
        return 'Not Sent';
    }
  };

  const getStatusIcon = (status: RefereeContact['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle style={{ fontSize: '14px', marginRight: '4px', color: '#2e7d32' }} />;
      case 'invited':
        return <Send style={{ fontSize: '14px', marginRight: '4px', color: '#1976d2' }} />;
      default:
        return undefined;
    }
  };

  return (
    <Box display='flex' justifyContent='center' flex='1 1 auto'>
      <Box maxWidth={720} width={1}>
        {/* Single Content Area */}
        <Box
          marginTop={2}
          marginBottom={3}
          paddingTop={3}
          paddingBottom={3}
          paddingLeft={4}
          paddingRight={2}
          border={'1px solid #e9e9e9'}
          bgcolor='#ffffff'
          borderRadius={7}
        >
          {/* Referees Header with Count and Add Button */}
          <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom={2}>
            <Typography variant='h6' style={{ fontWeight: 'bold' }}>
              Your Referees
            </Typography>
            <Box display='flex' alignItems='center' style={{ gap: 16 }}>
              {/* Count with Icons */}
              <Box display='flex' alignItems='center'>
                {Array.from({ length: requiredReferees }, (_unused, index) => (
                  <Box key={index} display='flex' alignItems='center' marginRight={0.5}>
                    {index < contacts.length ? (
                      <CheckCircle style={{ color: '#4caf50', fontSize: 16 }} />
                    ) : (
                      <RadioButtonUnchecked style={{ color: '#e0e0e0', fontSize: 16 }} />
                    )}
                  </Box>
                ))}
                <Typography variant='body2' color='textSecondary' style={{ marginLeft: 8 }}>
                  {contacts.length}/{requiredReferees} required
                </Typography>
              </Box>
              {!showAddForm && (
                <TabletButton
                  endIcon={<Add />}
                  variant='contained'
                  color='primary'
                  onClick={() => setShowAddForm(true)}
                  size='small'
                  disabled={hasReachedLimit}
                  title={hasReachedLimit ? `Maximum of ${requiredReferees} referees allowed` : undefined}
                >
                  Add Referee
                </TabletButton>
              )}
            </Box>
          </Box>

          {/* Empty State */}
          {contacts.length === 0 && !showAddForm && (
            <Box marginTop={3} marginBottom={3} textAlign='center'>
              <Typography variant='body2' color='textSecondary' style={{ marginBottom: '8px' }}>
                You need to add {requiredReferees} referee{requiredReferees !== 1 ? 's' : ''} for this task.
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                Referees will receive an email with a form to complete their reference.
              </Typography>
            </Box>
          )}

          {/* Current Referees List */}
          {contacts.length > 0 && (
            <Box marginTop={3} marginBottom={3}>
              {contacts.map((contact, index) => (
                <Box key={contact.id} style={{ marginBottom: '24px' }}>
                  {index > 0 && <Divider style={{ margin: '20px 0' }} />}

                  {/* First row: Name, Status, Delete button */}
                  <Box display='flex' alignItems='center' justifyContent='space-between' marginBottom={1}>
                    <Box display='flex' alignItems='center'>
                      <Typography variant='body1' style={{ fontWeight: 500 }}>
                        {contact.name}
                      </Typography>
                    </Box>
                    <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
                      <Chip
                        icon={getStatusIcon(contact.status)}
                        label={getStatusLabel(contact.status)}
                        size='small'
                        style={{
                          ...getStatusStyle(contact.status),
                          padding: '4px 12px',
                          height: '32px',
                          fontWeight: 500,
                        }}
                      />
                      <TabletButton
                        size='small'
                        variant='outlined'
                        onClick={() => handleRemoveReferee(contact.id, contact.name)}
                        style={{
                          minWidth: 'auto',
                          padding: '6px 8px',
                          borderColor: '#ff5252',
                          color: '#ff5252',
                        }}
                      >
                        <Delete style={{ fontSize: '18px', color: '#ff5252' }} />
                      </TabletButton>
                    </Box>
                  </Box>

                  {/* Second row: Email */}
                  <Box display='flex' alignItems='center' marginBottom={0.5}>
                    <Email style={{ marginRight: 6, color: '#757575', fontSize: '16px' }} />
                    <Typography variant='body2' color='textSecondary'>
                      {contact.email}
                    </Typography>
                  </Box>

                  {/* Third row: Phone */}
                  {contact.phone && (
                    <Box display='flex' alignItems='center'>
                      <Phone style={{ marginRight: 6, color: '#757575', fontSize: '16px' }} />
                      <Typography variant='body2' color='textSecondary'>
                        {contact.phone}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Add Referee Form */}
          {showAddForm && (
            <Box marginBottom={3}>
              <Typography variant='h6' gutterBottom style={{ fontWeight: 'bold' }}>
                Add Referee Contact
              </Typography>
              <Formik
                initialValues={{ refereeName: '', refereeEmail: '', refereePhone: '', refereeMessage: '' }}
                validationSchema={validationSchema}
                onSubmit={handleAddReferee}
              >
                {() => (
                  <Form>
                    <Box marginBottom={2}>
                      <Field
                        name='refereeName'
                        label='Referee Name'
                        placeholder='Full name of the referee'
                        required
                        fullWidth
                        variant='outlined'
                      />
                    </Box>
                    <Box marginBottom={2}>
                      <Field
                        name='refereeEmail'
                        label='Referee Email'
                        type='email'
                        placeholder='referee@example.com'
                        required
                        fullWidth
                        variant='outlined'
                      />
                    </Box>
                    <Box marginBottom={2}>
                      <Field
                        name='refereePhone'
                        label='Referee Phone Number (Optional)'
                        placeholder='+1 555 123 4567'
                        fullWidth
                        variant='outlined'
                      />
                    </Box>
                    <Box marginBottom={3}>
                      <Field
                        name='refereeMessage'
                        label='Personal Message (Optional)'
                        placeholder='e.g., Thank you for taking the time to provide a reference for me. I have worked with you on...'
                        fullWidth
                        variant='outlined'
                        multiline
                        rows={3}
                      />
                    </Box>
                    <Box display='flex' justifyContent='flex-end' width='100%'>
                      <TabletButton onClick={() => setShowAddForm(false)} style={{ marginRight: 8 }}>
                        Cancel
                      </TabletButton>
                      <TabletButton color='primary' variant='contained' type='submit' endIcon={<Add />}>
                        Add
                      </TabletButton>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
          )}

          {/* Send Button */}
          {contacts.length > 0 && (
            <Box display='flex' justifyContent='flex-end' marginTop={4}>
              {hasUninvitedContacts ? (
                <TabletButton
                  color='primary'
                  variant='contained'
                  endIcon={<Send />}
                  disabled={contacts.length < requiredReferees || isSending}
                  onClick={handleSendInvitations}
                >
                  {isSending ? 'Sending...' : 'Send Invitations'}
                </TabletButton>
              ) : hasInvitedContacts ? (
                <Tooltip title={getReminderTooltip()} arrow>
                  <span>
                    <TabletButton
                      color='primary'
                      variant='contained'
                      endIcon={<Send />}
                      disabled={!canSendReminder || isSending}
                      onClick={handleSendInvitations}
                    >
                      {isSending ? 'Sending...' : 'Send Reminder'}
                    </TabletButton>
                  </span>
                </Tooltip>
              ) : null}
            </Box>
          )}
        </Box>
      </Box>
      <ConfirmationDialog
        open={removeDialogOpen}
        title='Remove Referee'
        body={
          refereeToRemove
            ? `Are you sure you want to remove ${refereeToRemove.name} as a referee? This action cannot be undone.`
            : ''
        }
        closeActionText='Cancel'
        confirmActionText='Remove'
        handleCloseClick={() => setRemoveDialogOpen(false)}
        handleConfirmClick={confirmRemoveReferee}
      />
    </Box>
  );
};
